import React, { useState, useEffect } from "react";
import moment from "moment";
import { useFormik } from "formik";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormGroup,
} from "reactstrap";

import typography from "./typography";
import { dateTimeFormat } from "utils/dateFormatUtils";
import { CustomButton } from "components/CustomButton";
import CountrySelect from "../../components/CountrySelect";
import { DeviceType, SourceType } from "../../constants/ClickAnalyticsType";
import CustomDatePicker from "components/DatePicker";

const initialValues = {
  fromDate: null,
  toDate: null,
  devices: {
    ios: false,
    android: false,
    web: false,
  },
  sources: {
    facebook: false,
    instagram: false,
    other: false,
  },
  country: "",
  countryCode: "",
};

const FilterModal = (props) => {
  const { isOpen, onClose, onApply, initialFilters = initialValues } = props;
  const [deviceExpanded, setDeviceExpanded] = useState(false);
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const [countryExpanded, setCountryExpanded] = useState(false);

  const deviceOptions = [
    { key: "ios", label: DeviceType.IOS },
    { key: "android", label: DeviceType.ANDROID },
    { key: "web", label: DeviceType.WEB },
  ];

  const sourceOptions = [
    { key: "facebook", label: SourceType.FACEBOOK },
    { key: "instagram", label: SourceType.INSTAGRAM },
    { key: "other", label: SourceType.OTHER },
  ];

  const formik = useFormik({
    initialValues: initialFilters,
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};

      if (values.toDate && !values.fromDate) {
        errors.endDate = "Please select start date first";
      }

      // Validate date range
      if (values.fromDate && values.toDate) {
        const fromDate = moment(values.fromDate, dateTimeFormat.americanFormat);
        const toDate = moment(values.toDate, dateTimeFormat.americanFormat);

        if (fromDate.isAfter(toDate)) {
          errors.dateRange = "Start date must be before end date";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      await onApply(values);
    },
  });

  useEffect(() => {
    if (isOpen) {
      formik.setValues(initialFilters);
    }
  }, [isOpen, initialFilters]);

  const handleClear = () => {
    formik.setValues(initialValues);
    setDeviceExpanded(false);
    setSourceExpanded(false);
    setCountryExpanded(false);
  };

  const handleDeviceToggle = (deviceType, e) => {
    formik.setFieldValue(`devices.${deviceType}`, e.target.checked);
  };

  const handleSourceToggle = (sourceType, e) => {
    formik.setFieldValue(`sources.${sourceType}`, e.target.checked);
  };

  const handleCountryChange = (countryName, countryCode) => {
    formik.setFieldValue("country", countryName);
    formik.setFieldValue("countryCode", countryCode);
  };

  const isFormChanged = () => {
    return JSON.stringify(formik.values) !== JSON.stringify(initialFilters);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className="click-analytics-filter-modal"
      backdrop="static"
      centered
    >
      <ModalHeader className="filter-modal-header">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="filter-modal-title">Filter</div>
          <div className="filter-clear-btn" onClick={handleClear}>
            Clear
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="filter-modal-body">
        {/* Date Pickers */}
        <div className="filter-section">
          <div className="d-flex gap-3">
            <div className="flex-fill">
              <Label className="filter-date-label">From</Label>
              <CustomDatePicker
                selected={formik.values.fromDate}
                onChange={(val) => {
                  formik.setFieldValue("fromDate", val);
                  if (!val && formik.values.toDate) {
                    formik.setFieldValue("toDate", null);
                  }
                  if (formik.errors.dateRange) {
                    formik.setFieldError("dateRange", undefined);
                  }
                  if (formik.errors.endDate) {
                    formik.setFieldError("endDate", undefined);
                  }
                }}
                format={dateTimeFormat.americanFormat}
                placeholderText="Select date"
                className="form-control filter-date-input"
                disabled={false}
                minDate={null}
                maxDate={new Date()}
              />
            </div>
            <div className="flex-fill">
              <Label className="filter-date-label">To</Label>
              <CustomDatePicker
                selected={formik.values.toDate}
                onChange={(val) => {
                  formik.setFieldValue("toDate", val);
                  if (formik.errors.dateRange) {
                    formik.setFieldError("dateRange", undefined);
                  }
                  if (formik.errors.endDate) {
                    formik.setFieldError("endDate", undefined);
                  }
                }}
                format={dateTimeFormat.americanFormat}
                placeholderText="Select date"
                className="form-control filter-date-input"
                disabled={false}
                minDate={null}
                maxDate={new Date()}
              />
            </div>
          </div>
          {formik.errors.endDate && (
            <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
              {formik.errors.endDate}
            </div>
          )}
          {formik.errors.dateRange && (
            <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
              {formik.errors.dateRange}
            </div>
          )}
        </div>

        {/* Device Section */}
        <div className="filter-section">
          <div
            className="filter-collapsible-header"
            onClick={() => setDeviceExpanded(!deviceExpanded)}
          >
            <span className="filter-section-title">{typography.DEVICE}</span>
            <span
              className="filter-arrow"
              style={{
                transform: deviceExpanded ? "rotate(270deg)" : "rotate(90deg)",
              }}
            >
              {">"}
            </span>
          </div>
          {deviceExpanded && (
            <>
              {deviceOptions.map((option, index) => (
                <FormGroup
                  key={option.key}
                  className={index === 0 ? "my-2" : ""}
                >
                  <div className="form-check filter-checkbox-label">
                    <input
                      type="checkbox"
                      className="form-check-input filter-checkbox"
                      id={`device_${option.key}`}
                      defaultChecked={formik.values.devices[option.key]}
                      onChange={(e) => handleDeviceToggle(option.key, e)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`device_${option.key}`}
                    >
                      {option.label}
                    </label>
                  </div>
                </FormGroup>
              ))}
            </>
          )}
        </div>

        {/* Source Section */}
        <div className="filter-section">
          <div
            className="filter-collapsible-header"
            onClick={() => setSourceExpanded(!sourceExpanded)}
          >
            <span className="filter-section-title">{typography.SOURCE}</span>
            <span
              className="filter-arrow"
              style={{
                transform: sourceExpanded ? "rotate(270deg)" : "rotate(90deg)",
              }}
            >
              {">"}
            </span>
          </div>
          {sourceExpanded && (
            <>
              {sourceOptions.map((option, index) => (
                <FormGroup
                  key={option.key}
                  className={index === 0 ? "my-2" : ""}
                >
                  <div className="form-check filter-checkbox-label">
                    <input
                      type="checkbox"
                      className="form-check-input filter-checkbox"
                      id={`source_${option.key}`}
                      defaultChecked={formik.values.sources[option.key]}
                      onChange={(e) => handleSourceToggle(option.key, e)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`source_${option.key}`}
                    >
                      {option.label}
                    </label>
                  </div>
                </FormGroup>
              ))}
            </>
          )}
        </div>

        {/* Country Section */}
        <div className="filter-section">
          <div
            className="filter-collapsible-header"
            onClick={() => setCountryExpanded(!countryExpanded)}
          >
            <span className="filter-section-title">{typography.COUNTRY}</span>
            <span
              className="filter-arrow"
              style={{
                transform: countryExpanded ? "rotate(270deg)" : "rotate(90deg)",
              }}
            >
              {">"}
            </span>
          </div>
          {countryExpanded && (
            <div className="filter-country-select">
              <CountrySelect
                value={formik.values.country}
                onChange={handleCountryChange}
                placeholder="Select a country"
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="filter-modal-footer">
        <CustomButton
          title="Cancel"
          className="filter-cancel-btn"
          outline
          onClick={onClose}
        />
        <CustomButton
          title="Apply Filter"
          className="filter-apply-btn"
          onClick={formik.handleSubmit}
          disabled={
            !isFormChanged() ||
            !!formik.errors.dateRange ||
            !!formik.errors.endDate
          }
          loading={formik.isSubmitting}
        />
      </ModalFooter>
    </Modal>
  );
};

export default FilterModal;
